import { Injectable, OnDestroy, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { AppGlobalsModule } from '../app-globals.module';
import { Observable, throwError, Subject, BehaviorSubject } from 'rxjs';
import { Content } from '../content/content';
import { catchError, share, shareReplay, tap } from 'rxjs/operators';
import { YeshivaEnums } from 'src/serverTypes/yeshivaTypesEnums';
import { ErrorsHandler } from '../core/errors/errors-handler/errors-handler';
import { I18nService } from '../core/services/i18n/i18n.service';
import { ErrorsService } from '../core/errors/errors-service/errors.service';

export enum UserActionType {
    View = 'view',
    Favorite = 'favorite',
    History = 'history',
    Series = 'series',
}

@Injectable({
    providedIn: 'root',
})
export class UserDataService implements OnDestroy {
    protected http = inject(HttpClient);
    protected errorsHandler = inject(ErrorsHandler);
    protected _i18n = inject(I18nService);
    private errorService = inject(ErrorsService);

    protected contentUrl;
    private manageContent;
    private dataSummary;
    private userServerSummary;
    private userData$: Observable<any> | null = null;

    public invokeUpdateUserData: Subject<void>;
    // public activeUserMode = new BehaviorSubject<UserActionType>(null);

    constructor() {
        const _i18n = this._i18n;

        this.contentUrl = AppGlobalsModule.warehousePath('userField', _i18n.getLang());
        this.manageContent = AppGlobalsModule.warehousePath('saveContent', _i18n.getLang());
        this.dataSummary = AppGlobalsModule.warehousePath('dataSummary', _i18n.getLang());
        this.userServerSummary = AppGlobalsModule.userApiPath('UserAlerts') + '/';

        this.invokeUpdateUserData = new Subject<any>();
    }
    ngOnDestroy(): void {
        if (this.invokeUpdateUserData) {
            this.invokeUpdateUserData.unsubscribe();
        }
    }

    private fieldForAction(mode: UserActionType): string {
        if (mode === UserActionType.Favorite) {
            return 'contentfavorite';
        } else if (mode === UserActionType.History) {
            return 'contentHistory';
        } else if (mode === UserActionType.Series) {
            return 'registeredMidrashSerieses';
        }

        return 'contentHistory';
    }

    // setActiveUserMode(user_mode: UserActionType) {
    //     return this.activeUserMode.next(user_mode);
    // }

    // getActiveUserMode() {
    //     return this.activeUserMode.asObservable();
    // }

    /**
     * get warehose summary data:
     * series with viewed lessons ids (W)
     * number of favorits (W)
     * 2 last watch contents (W)
     *  -- number of rabbis
     *
     * get server summary data: (S)
     * number of unwatced answers / number of answers
     * number of replies
     * number of new replies alerts - /Users/UserAlerts/
     * subscriptions - types
     * -- donations
     */
    async getDataSummary() {
        let warehousePromise = this.fetchUserDataWarehouse().toPromise();
        let serverPromise = this.fetchUserDataServer().toPromise();
        let server;
        let warehouse;

        try {
            server = await serverPromise;
            warehouse = await warehousePromise;
        } catch (error) {
            this.errorService.log(error);
        }

        let summary: any = {};
        if (warehouse) {
            summary.warehose = warehouse;
        }

        if (server) {
            summary.server = server;
        }

        return summary;
    }

    /**
     * replies alerts - topic, title and time
     * series - id, % of watched, updated time
     * favorits - [10] - object
     * history - [10] - object, watch time
     * unwatced answers - object
     * new replies alerts - object
     * subscriptions - full data
     * -- rabbis - objects
     * -- donations - objects
     */
    getDataSummaryWithContents() {}

    fetchUserDataWarehouse(): Observable<any> {
        return this.http.get<any[]>(this.dataSummary, { withCredentials: true, headers: { noCache: 'true' } }).pipe(
            catchError<any, Observable<never>>((error) => {
                this.errorsHandler.handleError(error);
                return throwError(error);
            }),
        );
    }

    fetchUserDataServer(): Observable<any> {
        if (!this.userData$) {
            const params = new HttpParams().set('_v', '1');
            const options = { params: params, withCredentials: true };
            this.userData$ = this.http.post<any[]>(this.userServerSummary, options).pipe(
                shareReplay({ bufferSize: 1, refCount: true }),
                tap(() => {
                    // Can also be solvd with a SwitchMap but will require to change the way the userData to Subject.
                    AppGlobalsModule.setTimeoutIsNotServer(() => {
                        this.userData$ = null;
                    }, 3000);
                }),
                catchError<any, Observable<never>>((error) => {
                    this.errorsHandler.handleError(error);
                    return throwError(error);
                }),
            );
        }
        return this.userData$;
    }

    //invoke an update to user after data is change in backends
    public updateUserData(): void {
        this.invokeUpdateUserData.next();
    }

    /**
     * Get user field data from the house
     * won't work if user isn't log in
     */
    public getDataField(action: UserActionType, size: number = 0, params: HttpParams = new HttpParams()): Observable<any[]> {
        if (!params) {
            params = new HttpParams();
        }
        params = params.set('field', this.fieldForAction(action));

        // let headers =  new HttpHeaders();
        // headers = headers.set("noCache","true");

        return this.http
            .get<any[]>(this.contentUrl + '?', {
                params,
                withCredentials: true,
                headers: { noCache: 'true' },
            })
            .pipe(
                tap(() => this.updateUserData()),
                catchError<any, Observable<never>>((error) => {
                    this.errorsHandler.handleError(error);
                    return throwError(error);
                }),
            );
    }

    /**
     * Delete the whole record (all history, favorites etc)
     * @param action Which field has to be removed
     * @returns Observable of sucess or error message
     */
    public deleteDataField(action: UserActionType): Observable<any[]> {
        let params = new HttpParams();
        params = params.set('field', this.fieldForAction(action));

        return this.http.delete<Content[]>(this.contentUrl + '?', { params, withCredentials: true }).pipe(
            tap(() => this.updateUserData()),
            catchError<any, Observable<never>>((error) => {
                this.errorsHandler.handleError(error);
                return throwError(error);
            }),
        );
    }

    /**
     * Save user content view event to warehouse
     * @param TYPE Content's type
     * @param action Content's field
     * @param content Content's id
     */
    SaveContentView(TYPE: YeshivaEnums.YeshivaModels.BE.EntityType, action: UserActionType, content: number): Observable<any> {
        let params = new HttpParams();
        params = params
            .set('contenttype', '' + TYPE)
            .set('contentid', '' + content)
            .set('actiontype', '' + action)
            .set('language', this._i18n.getLangDigit().toString());

        return this.http
            .get<Content[]>(this.manageContent + '?', {
                params,
                withCredentials: true,
                headers: { noCache: 'true' },
            })
            .pipe(
                tap(() => this.updateUserData()),
                catchError<any, Observable<never>>((error) => {
                    this.errorsHandler.handleError(error);
                    return throwError(error);
                }),
            );
    }

    /**
     * Delete user content view event to warehouse
     * @param TYPE Content's type
     * @param action Content's field
     * @param content Content's id
     */
    DeleteContentview(TYPE: YeshivaEnums.YeshivaModels.BE.EntityType, action: UserActionType, content: number): Observable<any> {
        let params = new HttpParams();
        params = params
            .set('contenttype', '' + TYPE)
            .set('contentid', '' + content)
            .set('actiontype', '' + action)
            .set('language', this._i18n.getLangDigit().toString());

        return this.http.delete<Content[]>(this.manageContent + '?', { params, withCredentials: true }).pipe(
            tap(() => this.updateUserData()),
            catchError<any, Observable<never>>((error) => {
                this.errorsHandler.handleError(error);
                return throwError(error);
            }),
        );
    }
}
